import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { safeMaybeSingle } from "@/utils/supabase/queries";
import { isDatabaseRecord, toDatabaseValue } from "@/utils/supabase/type-guards";

export interface UserProfile {
  id: string;
  username: string;
  bio: string;
  tagline: string;
  longBio: string;
  focusTags: string[];
  externalLinks: Array<{ label: string; url: string }>;
  avatarId: number;
  isSubscriber: boolean;
  isDonator: boolean;
  subscriptionTier: string | null;
  createdAt: string;
  badges: Array<{
    id: string;
    name: string;
    description: string;
    earned_at: string;
  }>;
}

export const useUserProfile = (userId: string) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        // Fetch user profile
        // Use public_profiles view for security - only exposes non-sensitive fields
        const profileData = await safeMaybeSingle<any>(
          supabase
            .from("public_profiles")
            .select("*")
            .eq("id", toDatabaseValue(userId)),
          "Failed to fetch profile"
        );

        if (!profileData || !isDatabaseRecord(profileData)) {
          toast.error("User not found");
          setLoading(false);
          return;
        }

        setProfile({
          id: profileData.id,
          username: profileData.username,
          bio: profileData.bio || "",
          tagline: profileData.tagline || "",
          longBio: profileData.long_bio || "",
          focusTags: Array.isArray(profileData.focus_tags) ? profileData.focus_tags : [],
          externalLinks: Array.isArray(profileData.external_links) 
            ? (profileData.external_links as Array<{ label: string; url: string }>)
            : [],
          avatarId: profileData.avatar_id || 1,
          isSubscriber: profileData.is_subscriber || false,
          isDonator: profileData.is_donator || false,
          subscriptionTier: profileData.subscription_tier,
          createdAt: profileData.created_at,
          badges: profileData.badges || []
        });

      } catch (error: any) {
        console.error("Error fetching profile:", error);
        toast.error("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  return {
    profile,
    loading
  };
};
